import React from 'react';
import { DigitalTwin } from 'types/dt-graphql-apollo-v2';
import { IconType } from '@cognite/cogs.js';
import { getProjectGraphQLUrl } from 'utils/graphql-url';
import { Skeleton } from 'antd';
import styled from 'styled-components/macro';
import { useUrl } from 'hooks/useUrls';
import { ISideBarTab, SideBarMenu } from 'components';
import { Route, Switch } from 'react-router-dom';
import { getDigitalTwinUrl, ROUTE_DIGITAL_TWIN } from 'utils/router-url';
import { useStatefulHistory } from 'hooks/history';
import { useGetProjectQuery } from 'hooks/projects';
import { SchemaPlayground } from './SchemaPlayground';
import { SchemaView } from './SchemaView';
import { DataValidation } from './DataValidation/DataValidation';
import { ProjectRuleset } from './ProjectRuleset/ProjectRuleset';

interface ViewSchemaPageProps {
  digitalTwin: DigitalTwin;
}
export const ViewSchemaPage = ({ digitalTwin }: ViewSchemaPageProps) => {
  const schemaNameDT = useUrl.SchemaNameDT();
  const tenant = useUrl.Tenant();
  const digitalTwinId = useUrl.DigitalTwinId();
  const history = useStatefulHistory();
  const currentSchema = digitalTwin?.schemaCollections?.items.find(
    (schema) => schema?.name === schemaNameDT
  );

  const { data } = useGetProjectQuery(currentSchema?.refId);

  if (!currentSchema) {
    return <Skeleton />;
  }

  let schemaIsEmpty = true;
  if (data?.Project && data.Project[0]?.graphQLSchemaString) {
    schemaIsEmpty = !data.Project[0]?.graphQLSchemaString.trim();
  }

  type Tabs = {
    title: string;
    slug: string;
    disabled?: boolean;
    onClick?: VoidFunction;
    icon?: IconType;
  };
  const tabs: Tabs[] = [
    {
      slug: 'schema_view',
      title: 'Schema',
      icon: 'DataTable',
    },
    {
      slug: 'playground',
      title: 'Query explorer',
      icon: 'Comment',
      disabled: schemaIsEmpty,
    },
    {
      slug: 'data_validation',
      title: 'Runs',
      icon: 'LineChart',
      disabled: schemaIsEmpty,
    },
    {
      slug: 'rulesets',
      title: 'Rulesets',
      icon: 'Document',
      disabled: schemaIsEmpty,
    },
  ];
  const links: ISideBarTab[] = tabs.map((tab) => ({
    disabled: tab.disabled,
    tooltip: tab.disabled
      ? `You need to set a schema first to use the ${tab.title}`
      : tab.title,
    name: tab.title,
    isActive: history.location.pathname.includes(`${tab.slug}`),
    onClick: () => {
      if (tab.onClick) {
        tab.onClick();
      }
      history.pushStateful(
        `${getDigitalTwinUrl(tenant, digitalTwinId)}/schemas/${schemaNameDT}/${
          tab.slug
        }`
      );
    },
    icon: tab.icon,
  }));

  const SchemaViewRouter = () => {
    const url = getProjectGraphQLUrl(currentSchema.refId);
    return (
      <Switch>
        <Route
          path={`${ROUTE_DIGITAL_TWIN}/schemas/:schemaNameDT?/schema_view`}
        >
          <SchemaView schema={currentSchema} />
        </Route>
        <Route path={`${ROUTE_DIGITAL_TWIN}/schemas/:schemaNameDT?/playground`}>
          <SchemaPlayground url={url} />
        </Route>
        <Route
          path={`${ROUTE_DIGITAL_TWIN}/schemas/:schemaNameDT?/data_validation`}
        >
          <DataValidation schema={currentSchema} />
        </Route>
        <Route path={`${ROUTE_DIGITAL_TWIN}/schemas/:schemaNameDT?/rulesets`}>
          <ProjectRuleset schemaCollection={currentSchema} />
        </Route>
      </Switch>
    );
  };

  return (
    <StyledContainer>
      <StyledSidePanel>
        <SideBarMenu
          title={currentSchema?.name ? currentSchema?.name : ''}
          tabs={links}
        />
      </StyledSidePanel>
      <StyledContentPanel>{SchemaViewRouter()}</StyledContentPanel>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  background-color: #fff;
`;

const StyledSidePanel = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledContentPanel = styled.div`
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;
